import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TABLE_TYPE_OPTIONS } from "../../../types";
import { selectRowModal } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { ADD_ON_FORM_I18N_PREFIX, ADD_ON_TABLE_COLUMN_LABELS } from "../data";
import { useStyles } from "./styles";

const cols = ADD_ON_TABLE_COLUMN_LABELS;
const noteKey = `${ADD_ON_FORM_I18N_PREFIX}.FIELDS.NOTE`;

const TableRowModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const modal = useSelector(selectRowModal);

  if (!modal.open || !modal.tableKey) {
    return null;
  }

  const patch = (field: "location" | "itemType" | "quantity" | "note", value: string) =>
    dispatch(actions.patchRowModal({ field, value }));

  const stepQty = (delta: number) => {
    const next = (Number(modal.quantity) || 0) + delta;
    patch("quantity", String(next < 0 ? 0 : next));
  };

  const canSubmit = modal.location.trim().length > 0 && modal.itemType.length > 0;

  return (
    <Modal open={modal.open} onClose={() => dispatch(actions.closeRowModal())}>
      <Box className={styles.rowModal}>
        <Box className={styles.rowModalHeader}>
          <Typography className={styles.rowModalTitle}>{t(modal.titleKey)}</Typography>
          <IconButton
            className={styles.rowModalClose}
            onClick={() => dispatch(actions.closeRowModal())}
            aria-label={t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.rowModalBody}>
          <Box className={styles.rowModalField}>
            <Typography className={styles.rowModalLabel}>{t(cols.location)}</Typography>
            <TextField
              className={styles.rowModalInput}
              fullWidth
              variant="outlined"
              placeholder={t(cols.location)}
              value={modal.location}
              InputProps={{ notched: false }}
              onChange={(e) => patch("location", e.target.value)}
            />
          </Box>

          <Box className={styles.rowModalFieldsRow}>
            <Box className={`${styles.rowModalField} ${styles.rowModalFieldGrow}`}>
              <Typography className={styles.rowModalLabel}>{t(cols.itemType)}</Typography>
              <FormControl fullWidth className={styles.rowModalSelectControl}>
                <Select
                  className={styles.rowModalSelect}
                  variant="outlined"
                  displayEmpty
                  value={modal.itemType}
                  IconComponent={DropdownIcon}
                  onChange={(e) => patch("itemType", e.target.value as string)}
                  renderValue={() => (
                    <span className={styles.rowModalSelectValue}>
                      {modal.itemType ? t(modal.itemType) : t(cols.itemType)}
                    </span>
                  )}
                >
                  {TABLE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className={styles.rowModalFieldNarrow}>
              <Typography className={styles.rowModalLabel}>{t(cols.quantity)}</Typography>
              <Box className={styles.rowModalStepper}>
                <Box className={styles.rowModalStepperArrows}>
                  <KeyboardArrowUpIcon
                    className={styles.rowModalStepperArrow}
                    onClick={() => stepQty(1)}
                  />
                  <KeyboardArrowDownIcon
                    className={styles.rowModalStepperArrow}
                    onClick={() => stepQty(-1)}
                  />
                </Box>
                <Typography className={styles.rowModalStepperValue}>
                  {modal.quantity}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className={styles.rowModalField}>
            <Typography className={styles.rowModalLabel}>{t(noteKey)}</Typography>
            <TextField
              className={styles.rowModalInput}
              fullWidth
              variant="outlined"
              placeholder={t(noteKey)}
              value={modal.note}
              InputProps={{ notched: false }}
              onChange={(e) => patch("note", e.target.value)}
            />
          </Box>
        </Box>

        <Box className={styles.rowModalFooter}>
          <Button
            className={styles.rowModalCancelBtn}
            variant="outlined"
            onClick={() => dispatch(actions.closeRowModal())}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.rowModalAddBtn}
            variant="contained"
            disableElevation
            disabled={!canSubmit}
            onClick={() => dispatch(actions.submitRowModal())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TableRowModal;
